/**
 * LiveRoom Component
 * The core immersive experience - artist centered with floating listeners and spatial chat
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, Pause, Radio, Users, Mic } from 'lucide-react';
import EnergyVisualizer from './EnergyVisualizer';
import ListenerAvatar from './ListenerAvatar';
import SpatialChat from './SpatialChat';
import ActionBar from './ActionBar';
import { cn } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LiveRoom = ({ 
  room, 
  isPlaying, 
  onPlay, 
  onPause, 
  energyLevel, 
  listeners,
  chatMessages,
  currentTrack,
  isSnippetMode = false,
  onViewStory,
  interfaceMode = 'classic',
  navHeight = 0
}) => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const isLive = room?.isLive;
  const [artistHasStory, setArtistHasStory] = useState(false);
  const [artistGlowColor, setArtistGlowColor] = useState('#FF1493');
  
  // Check if this is the current user's own artist profile
  const isOwnProfile = user?.id && room?.artistUserId === user.id;

  // The song currently playing in THIS room (rooms are grouped by a single
  // artist, so the playing track's artist == this room's artist). When a song
  // is playing we show that SONG's artwork in the center (not the artist's
  // profile pic / first-track art) and the click still routes to the artist
  // who shared it.
  const thisRoomPlaying = currentTrack && (
    currentTrack.artist_id === room?.id ||
    currentTrack.uploader_id === room?.id ||
    (room?.tracks || []).some(t => (t.id || t.track_id) === (currentTrack.id || currentTrack.track_id))
  );
  const activeArtwork = (thisRoomPlaying && currentTrack.artwork) || room?.artistImage;

  // Subtle tap feedback — short vibration on supported devices (mobile)
  const haptic = () => { try { navigator.vibrate && navigator.vibrate(12); } catch (e) {} };

  // Reset story state when room changes
  useEffect(() => {
    setArtistHasStory(false);
    setArtistGlowColor('#FF1493');
  }, [room?.id]);

  // Check if artist has active stories (only for valid user IDs)
  useEffect(() => {
    // Skip if no artistUserId or if it's an admin-uploaded placeholder
    if (!room?.artistUserId || !token) {
      setArtistHasStory(false);
      return;
    }
    if (room.artistUserId.startsWith('admin_uploaded')) {
      setArtistHasStory(false);
      return;
    }
    
    const checkArtistStory = async () => {
      try {
        console.log('Checking stories for artist:', room.artistUserId, room.artistName);
        const res = await fetch(`${API_URL}/api/radio/stories/check-glow/${room.artistUserId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          console.log('Story check result:', data);
          setArtistHasStory(data.has_stories || false);
          if (data.has_stories) {
            setArtistGlowColor(data.glow_color || '#FF1493');
          }
        } else {
          setArtistHasStory(false);
        }
      } catch (err) {
        console.error('Story check error:', err);
        setArtistHasStory(false);
      }
    };
    checkArtistStory();
  }, [room?.artistUserId, room?.id, token]);

  // Handle artist image click - view story if has one, otherwise go to profile
  const handleArtistClick = async () => {
    haptic();
    console.log('Artist click:', {
      artistUserId: room?.artistUserId,
      artistName: room?.artistName,
      artistHasStory: artistHasStory,
      isOwnProfile: isOwnProfile
    });
    
    if (!room?.artistUserId) return;
    
    // Don't navigate for admin-uploaded content - no real profile exists
    if (room.artistUserId.startsWith('admin_uploaded')) {
      console.log('Blocked: admin-uploaded content');
      return;
    }
    
    // For own profile, just navigate to profile (don't show story viewer)
    if (isOwnProfile) {
      console.log('Own profile - navigating to profile');
      navigate(`/profile/${room.artistUserId}`);
      return;
    }
    
    if (artistHasStory && onViewStory) {
      // Has story - fetch and show it
      try {
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        console.log('Fetching stories for:', room.artistUserId);
        const res = await fetch(`${API_URL}/api/radio/stories/user/${room.artistUserId}`, { headers });
        if (res.ok) {
          const data = await res.json();
          console.log('Fetched stories:', data.stories?.length);
          if (data.stories?.length > 0) {
            onViewStory({
              user_id: room.artistUserId,
              user_name: room.artistName,
              user_photo: room.artistImage,
              is_artist: true,
              artist_name: room.artistName,
              stories: data.stories
            });
            return;
          }
        }
      } catch (err) {
        console.error('Error fetching artist stories:', err);
      }
    }
    
    // No story or fetch failed - go to artist profile
    console.log('Navigating to profile:', room.artistUserId);
    navigate(`/profile/${room.artistUserId}`);
  };

  // Always-available profile navigation (so a live story never blocks profile access)
  const goToProfile = (e) => {
    if (e) e.stopPropagation();
    haptic();
    if (!room?.artistUserId || room.artistUserId.startsWith('admin_uploaded')) return;
    navigate(`/profile/${room.artistUserId}`);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 1.05 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="absolute inset-0 flex items-center justify-center"
      style={{ paddingTop: (navHeight ? navHeight + 52 : 180), paddingBottom: 236 }}
      data-testid={`live-room-${room?.id}`}
    >
      {/* Background Gradient */}
      <div 
        className="absolute inset-0 transition-opacity duration-1000"
        style={{
          background: `radial-gradient(circle at center, 
            ${isLive ? 'rgba(255, 0, 127, 0.15)' : 'rgba(0, 240, 255, 0.1)'} 0%, 
            transparent 70%)`
        }}
      />

      {/* Floating Listeners - Hide in focus mode, show less in feed mode */}
      {interfaceMode !== 'focus' && listeners.slice(0, interfaceMode === 'feed' ? 5 : listeners.length).map((listener, index) => (
        <ListenerAvatar
          key={listener.id}
          listener={listener}
          index={index}
          chatMessages={chatMessages.filter(m => m.listenerId === listener.id)}
          onViewStory={onViewStory}
        />
      ))}

      {/* Central Artist */}
      <div className="relative z-10 flex items-center justify-center">
        {/* Energy Visualizer - positioned behind the artist */}
        <div className="absolute" style={{ width: 320, height: 320 }}>
          <EnergyVisualizer 
            energyLevel={energyLevel} 
            isPlaying={isPlaying}
            isLive={isLive}
          />
        </div>

        <motion.div
          className={cn("relative z-20 flex flex-col items-center", isLive && "scale-105")}
        >
          {/* On-Air Glow */}
          {isLive && (
            <motion.div
              className="absolute -inset-4 rounded-full pointer-events-none"
              animate={{
                boxShadow: [
                  '0 0 30px rgba(255, 0, 127, 0.4)',
                  '0 0 60px rgba(255, 0, 127, 0.6)',
                  '0 0 30px rgba(255, 0, 127, 0.4)',
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          )}

          {/* Artist Image */}
          <div className="relative">
            {/* Story Glow Ring - Click to view story (only for other artists, not own) */}
            {artistHasStory && !isOwnProfile && (
              <motion.div
                className="absolute -inset-2 rounded-full cursor-pointer z-10"
                style={{ 
                  background: `linear-gradient(135deg, ${artistGlowColor}, ${artistGlowColor}88)`,
                  boxShadow: `0 0 25px ${artistGlowColor}66`
                }}
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                onClick={handleArtistClick}
              />
            )}
            
            {/* Clickable area for profile/story */}
            <motion.div 
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              className="relative cursor-pointer"
              onClick={handleArtistClick}
              data-testid="artist-profile-btn"
            >
              <div className={cn(
                "w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-4 transition-all duration-300",
                artistHasStory
                  ? "border-transparent"
                  : isLive 
                    ? "border-[#FF007F] shadow-[0_0_40px_rgba(255,0,127,0.5)]" 
                    : "border-[#00F0FF]/50 hover:border-[#00F0FF]"
              )}>
                {activeArtwork ? (
                  <img src={activeArtwork} alt={currentTrack?.track_title || room?.artistName} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#00F0FF] to-[#FF007F] flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">{room?.artistName?.charAt(0) || '?'}</span>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Play/Pause Button - Separate from profile click */}
            <motion.button
              whileTap={{ scale: 0.85 }}
              transition={{ type: 'spring', stiffness: 500, damping: 22 }}
              onClick={(e) => { e.stopPropagation(); haptic(); isPlaying ? onPause() : onPlay(); }}
              className="absolute bottom-0 right-0 z-20 w-10 h-10 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/20 hover:border-white/40 transition"
              data-testid="artist-play-btn"
            >
              {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
            </motion.button>

            {/* Live Badge */}
            {isLive && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-2 -right-2 px-3 py-1 bg-[#FF007F] rounded-full flex items-center gap-1.5 shadow-lg z-20"
              >
                <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                <span className="text-white text-xs font-bold tracking-wide">LIVE</span>
              </motion.div>
            )}
            
            {/* Label - View Story / View Profile (a separate Profile chip when a story is up) */}
            {!room?.artistUserId?.startsWith('admin_uploaded') && (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1.5 whitespace-nowrap">
                {isOwnProfile ? (
                  <button onClick={goToProfile} data-testid="view-profile-label"
                    className="px-3 py-1 bg-black/60 backdrop-blur-sm rounded-full text-xs font-medium text-white cursor-pointer">My Profile</button>
                ) : artistHasStory ? (
                  <>
                    <button onClick={handleArtistClick} data-testid="view-story-label"
                      className="px-3 py-1 bg-black/60 backdrop-blur-sm rounded-full text-xs font-medium text-white cursor-pointer">View Story</button>
                    <button onClick={goToProfile} data-testid="view-profile-label"
                      className="px-3 py-1 bg-[#00F0FF]/20 text-[#00F0FF] backdrop-blur-sm rounded-full text-xs font-medium cursor-pointer">Profile</button>
                  </>
                ) : (
                  <button onClick={goToProfile} data-testid="view-profile-label"
                    className="px-3 py-1 bg-black/60 backdrop-blur-sm rounded-full text-xs font-medium text-white cursor-pointer">View Profile</button>
                )}
              </motion.div>
            )}
          </div>

          {/* Artist Name */}
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6 text-2xl md:text-3xl font-bold text-white tracking-tight text-center"
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            {room?.artistName}
          </motion.h2>

          {/* Now Playing */}
          {thisRoomPlaying && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative z-30 mt-2 flex flex-col items-center"
            >
              <p className="text-[#A1A1AA] text-sm">
                Playing: <span className="text-[#00F0FF] font-medium">{currentTrack.track_title}</span>
              </p>
              <button
                onClick={handleArtistClick}
                className="mt-1 text-xs text-white/50 hover:text-white/80 transition"
                data-testid="view-artist-hint"
              >
                Tap the cover to view {currentTrack.artist_name || room?.artistName}'s profile →
              </button>
            </motion.div>
          )}

          {/* Stats - Hide in focus mode */}
          {interfaceMode !== 'focus' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-4 flex items-center gap-6"
            >
              <div className="flex items-center gap-2 text-[#A1A1AA]">
                <Users className="w-4 h-4" />
                <span className="text-sm font-medium">{room?.listenerCount?.toLocaleString() || listeners.length}</span>
              </div>
              <div className="px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm">
                <span className="text-xs text-white/80 uppercase tracking-wider">{room?.genre || 'Hip-Hop'}</span>
              </div>
              <div className="flex items-center gap-2 text-[#A1A1AA]">
                <Radio className="w-4 h-4" />
                <span className="text-sm font-medium">{room?.tracks?.length || 0} tracks</span>
              </div>
            </motion.div>
          )}

          {/* Live Indicator - Always show but smaller in focus mode */}
          {isLive && (
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className={cn(
                "mt-4 flex items-center gap-2 rounded-full bg-[#FF007F]/20 border border-[#FF007F]/30",
                interfaceMode === 'focus' ? "px-3 py-1" : "px-4 py-2"
              )}
            >
              <Mic className={cn("text-[#FF007F] animate-pulse", interfaceMode === 'focus' ? "w-3 h-3" : "w-4 h-4")} />
              <span className={cn("text-[#FF007F] font-medium", interfaceMode === 'focus' ? "text-xs" : "text-sm")}>
                {interfaceMode === 'focus' ? 'LIVE' : 'Broadcasting Live'}
              </span>
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Spatial Chat - Hide in focus mode */}
      {interfaceMode !== 'focus' && (
        <SpatialChat messages={chatMessages} listeners={listeners} />
      )}

      {/* Action Bar with Voting - Show compact in focus mode */}
      <ActionBar room={room} currentTrack={currentTrack} compact={interfaceMode === 'focus'} />
    </motion.div>
  );
};

export default LiveRoom;
